import React, { useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import SignalIcon from "./SignalIcon";
import { AccountLogo } from "./AccountLogo";
import { AlsoReach, Chips, CompetitorTag, OwnerBlock, PostedBy, RoleLine, WhyItMatters } from "./HiringParts";
import { timeAgo, hiringOf, firstNameOf, draftStateOf, draftRecipient } from "./signalMeta";
import { draftEmail, findEmailFromLinkedIn, reviewDraft, reviewLabel } from "./feedActions";
import DraftLine from "./DraftLine";

/**
 * A hiring intel entry in the feed: the account and what it is building, its tech and skills, who is hiring, and the
 * roles behind it. Acts on the hiring owner: draft them an email (finding it first when we don't have it), add them
 * to a list, open the post.
 */
const HiringRow = ({ item, onPickList }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // What a lookup found for the owner on this page ({email, contactId}).
  const [found, setFound] = useState(null);
  const e = item.event || {};
  const h = hiringOf(item);
  const owner = h.owner ? { ...h.owner, ...(found || {}) } : null;
  const first = owner ? firstNameOf(owner.name) : "";
  const unread = !e.read && e.state === "OPEN";
  const handled = e.state === "HANDLED" || e.state === "DISMISSED" || !!(item.actionType || e.actionType);
  const accountName = h.accountName || item.entityName || "An account";
  // A competitor's hiring is read, not acted on: no reach or draft actions, just the post.
  const competitor = h.isCompetitor;
  // A recruiter's or a peer's post: the persona card is who to reach, so it becomes the main button.
  const findPersona = (h.ownerIsRecruiter || h.ownerIsPeer) && !!h.persona && !competitor;
  const domain = item.entityDomain || (item.entity || {}).domain || "";
  const source = signalsData.saved.find(s => s.signal && s.signal.id === e.savedSignalId);

  const markRead = () => unread && signalsData.act(e.id, "read");
  const openAccount = () => {
    markRead();
    history.push(`/accounts/${e.entityId}/overview`);
  };
  const busyWhile = async fn => {
    setBusy(true);
    try { return await fn(); } finally { setBusy(false); }
  };
  // The owner's email and contact: what we have, else a lookup from their LinkedIn page (saves them as a contact).
  const lookUp = async () => {
    if (owner.email) return owner;
    if (!owner.linkedinUrl) return null;
    const hit = await findEmailFromLinkedIn({ ...owner, company: accountName, domain });
    if (hit) setFound(hit);
    return hit ? { ...owner, ...hit } : null;
  };

  const draft = () => busyWhile(async () => {
    const who = await lookUp();
    if (who && who.email && (await draftEmail(who.email, history))) markRead();
  });
  const toList = () => busyWhile(async () => {
    const who = owner.contactId ? owner : await lookUp();
    if (who && who.contactId) onPickList({ ...item, entity: { ...(item.entity || {}), contactId: who.contactId } });
  });
  const viewPost = () => {
    markRead();
    window.open(h.postUrl, "_blank", "noopener,noreferrer");
  };
  const dismiss = async () => {
    if (await signalsData.recordAction(e.id, "dismiss")) toasterState.setToastMsg("Dismissed", "success");
  };
  // No owner, or one we can't reach from here: the account page has the people.
  const canReach = owner && (owner.email || owner.linkedinUrl);
  const mainLabel = canReach && owner.email ? `Draft email to ${first}` : "Find email";

  // The suggested email. A recruiter's post: it is for the persona, so the people card picks who it goes to first.
  const draftState = competitor ? "none" : draftStateOf(item);
  const ownerDraft = !findPersona && draftState === "ready";
  const recipient = draftRecipient({ ...(owner || {}), company: accountName, domain }, item.draft);
  const openDraft = draft => busyWhile(async () => {
    const sent = await reviewDraft(draft, draftRecipient(recipient, draft), history);
    if (sent) {
      if (owner && !owner.email) setFound({ email: sent.email, contactId: sent.contactId || null });
      markRead();
    }
  });
  const askDraft = () => busyWhile(async () => {
    const got = await signalsData.fetchDraft(e.id);
    // A persona draft waits for the people card; an owner's opens straight away.
    if (got && got.draftStatus === "ready" && got.draft && !findPersona) await openDraft(got.draft);
  });
  const reachable = r => !!(r.email || r.contactId || r.linkedinUrl);
  // With nobody to reach, the account's name opens it; no separate Open button.
  let mainButton = canReach ? (
    <button className="sg-btn sg-btn--small" disabled={busy} onClick={draft}>{mainLabel}</button>
  ) : null;
  // A ready draft is sent from inside its preview card (DraftLine), so the corner has no second send button.
  let draftAction = null;
  if (draftState === "ready" && findPersona) {
    draftAction = <AlsoReach asButton persona={h.persona} domain={domain} accountId={e.entityId} accountName={accountName} draft={item.draft} onDrafted={markRead} />;
  } else if (ownerDraft && reachable(recipient)) {
    draftAction = <button className="sg-btn sg-btn--small" disabled={busy} onClick={() => openDraft(item.draft)}>{reviewLabel(recipient)}</button>;
    mainButton = null;
  } else if (!findPersona && draftState === "pending") {
    mainButton = <button className="sg-btn sg-btn--small" disabled={busy} onClick={askDraft}>Draft email</button>;
  }

  return (
    <div className={`sg-frow sg-hire ${unread ? "is-unread" : ""} ${handled ? "is-handled" : ""}`}>
      <div className="sg-frow__who sg-hire__who">
        <span className="sg-frow__av">
          <AccountLogo domain={domain} name={accountName} />
          <span className="sg-frow__badge acc"><SignalIcon id="hiring_intel" size={10} strokeWidth={2.2} /></span>
        </span>
        <div className="sg-grow">
          <div className="sg-hire__acct"><button type="button" className="sg-namelink" onClick={openAccount}>{accountName}</button>{competitor && <CompetitorTag />}</div>
          <div className="sg-hire__sum">{h.summary || "New hiring at this account"}</div>
          {(h.technologies.length > 0 || h.skills.length > 0) && (
            <div className="sg-hire__chips">
              <Chips items={h.technologies} />
              <Chips items={h.skills} max={3} kind="sg-hchips--skills" />
            </div>
          )}
          <WhyItMatters why={h.why} competitor={competitor} />
          {!competitor && (owner || h.persona || h.poster) && (
            <div className="sg-hire__people">
              {owner && <OwnerBlock owner={owner} />}
              <PostedBy poster={h.poster} peer={h.ownerIsPeer} />
              {!findPersona && <AlsoReach persona={h.persona} domain={domain} accountId={e.entityId} accountName={accountName} />}
              {/* A recruiter's or a peer's post: the persona is who to reach; the link and the row's button open the same picker. */}
              {findPersona && (
                <span className="sg-halso">
                  Who to reach: <AlsoReach label={h.persona} persona={h.persona} domain={domain} accountId={e.entityId} accountName={accountName} draft={draftState === "ready" ? item.draft : null} onDrafted={markRead} />
                </span>
              )}
            </div>
          )}
          {!competitor && !handled && <DraftLine item={item} who={findPersona ? h.persona : (owner && owner.name) || ""} action={draftAction} />}
          {h.roles.length > 0 && (
            <button className={`sg-hire__toggle ${open ? "is-open" : ""}`} aria-expanded={open} onClick={() => setOpen(!open)}>
              {h.roles.length} role{h.roles.length === 1 ? "" : "s"} <SignalIcon id="chevron" size={13} />
            </button>
          )}
        </div>
      </div>
      <div className="sg-frow__from"><span>{(source && source.displayName) || item.savedSignalName || "Hiring intel"}</span></div>
      <div className="sg-frow__when">{timeAgo(e.detectedAt)}</div>
      <div className="sg-frow__acts">
        {findPersona ? !draftAction && (
          <AlsoReach
            asButton
            persona={h.persona}
            domain={domain}
            accountId={e.entityId}
            accountName={accountName}
            draft={draftState === "ready" ? item.draft : null}
            onDrafted={markRead}
          />
        ) : !competitor && mainButton}
        {owner && !competitor && (
          <button className="sg-ibtn sg-ibtn--grow" aria-label={`Add ${first} to a list`} disabled={busy || !canReach} onClick={toList}>
            <SignalIcon id="listAdd" size={15} /><span className="sg-ibtn__lbl">Add to list</span>
          </button>
        )}
        {h.postUrl && (
          <button className="sg-ibtn sg-ibtn--grow" aria-label="View post" onClick={viewPost}><SignalIcon id="external" size={14} /><span className="sg-ibtn__lbl">View post</span></button>
        )}
        {!handled && <button className="sg-link sg-link--quiet" disabled={busy} onClick={dismiss}>Dismiss</button>}
      </div>
      {open && (
        <div className="sg-hroles">
          {h.roles.map((r, i) => <RoleLine key={`${r.title}-${i}`} role={r} />)}
        </div>
      )}
    </div>
  );
};

/** While a new hiring watch runs its first check: a shimmering row in place of the entries to come. */
export const FirstCheckRow = ({ accounts }) => (
  <div className="sg-frow sg-hire-wait" role="status">
    <span className="sg-hire-wait__bar" aria-hidden="true" />
    <span>
      Reading hiring posts at {accounts ? `${Number(accounts).toLocaleString()} account${Number(accounts) === 1 ? "" : "s"}` : "your accounts"}…
    </span>
  </div>
);

export default observer(HiringRow);
